import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import MarketingFooter from "@/components/MarketingFooter";
import { motion } from "framer-motion";
import { usePageMeta } from "@/lib/pageMeta";
import {
  Sparkles, ShieldCheck, BrainCircuit, Heart, ChevronRight,
  Moon, ArrowRight, Users, Feather, Quote, Star, Lock, TrendingUp, Briefcase
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const HERO_COUPLE_POSTER = "https://images.unsplash.com/photo-1591604466107-ec97de577aff?fm=jpg&w=1600&auto=format&fit=crop&q=80";
const HERO_VIDEO = "https://videos.pexels.com/video-files/6853347/6853347-uhd_2560_1440_25fps.mp4";

const STARS = Array.from({ length: 55 }, () => {
  const top = Math.random() * 100;
  const left = Math.random() * 100;
  return {
    id: `star-${top.toFixed(3)}-${left.toFixed(3)}`,
    top, left,
    size: Math.random() * 2.6 + 1,
    delay: Math.random() * 4,
    duration: 3 + Math.random() * 4,
  };
});

function StarField() {
  return (
    <div className="starfield" aria-hidden>
      {STARS.map((s) => (
        <span key={s.id} className="star" style={{
          top: `${s.top}%`, left: `${s.left}%`, width: `${s.size}px`, height: `${s.size}px`,
          animationDelay: `${s.delay}s`, animationDuration: `${s.duration}s`,
        }} />
      ))}
    </div>
  );
}

function VideoHero() {
  const [ok, setOk] = useState(true);
  return (
    <div className="relative rounded-[2rem] overflow-hidden border border-stone-200 shadow-[0_30px_80px_-30px_rgba(30,27,75,0.35)]">
      {ok ? (
        <video
          className="w-full h-[540px] object-cover"
          src={HERO_VIDEO}
          poster={HERO_COUPLE_POSTER}
          autoPlay muted loop playsInline
          onError={() => setOk(false)}
        />
      ) : (
        <img src={HERO_COUPLE_POSTER} alt="Thoughtful couple" className="w-full h-[540px] object-cover" />
      )}
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 45%, rgba(30,27,75,0.55) 100%)" }} />
      <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
        <div className="bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase text-[#1E1B4B] flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Verified · Live match
        </div>
        <div className="text-white/80 text-xs">Report #A-0714</div>
      </div>
    </div>
  );
}

const STATS = [
  { n: "847", l: "Marriage Intelligence Reports generated" },
  { n: "123", l: "Successful matches" },
  { n: "340", l: "Active members" },
  { n: "91%", l: "Profile compatibility accuracy" },
  { n: "28", l: "Cities covered" },
];

const PILLARS = [
  { icon: ShieldCheck, title: "Verified Profiles", desc: "Every profile is ID + selfie verified. What your parents see is real." },
  { icon: BrainCircuit, title: "Psychology-First", desc: "18 questions across 8 dimensions, grounded in relationship science." },
  { icon: Moon, title: "Vedic + AI", desc: "Kundali Ashtakoot fused with AI compatibility in one report." },
  { icon: Heart, title: "Family-Ready", desc: "Beautiful reports designed to be shared, discussed, and blessed." },
];

const EXPLORE = [
  {
    to: "/how-it-works",
    tag: "The Method",
    title: "How it works",
    body: "Four thoughtful steps — Verified You, Assessment, Fusion Report, Family Share.",
    icon: Sparkles,
    testid: "explore-how",
  },
  {
    to: "/kundali",
    tag: "Fusion",
    title: "Kundali × AI",
    body: "36 gunas of Vedic wisdom + 8 dimensions of relationship science in one report.",
    icon: Moon,
    testid: "explore-kundali",
  },
  {
    to: "/family",
    tag: "Family",
    title: "Family Circle",
    body: "Permission-based, revocable family view. Elders as first-class users.",
    icon: Users,
    testid: "explore-family",
  },
  {
    to: "/philosophy",
    tag: "Why we built this",
    title: "Philosophy",
    body: "Six beliefs about marriage, family, and what a good matchmaker actually owes you.",
    icon: Feather,
    testid: "explore-philosophy",
  },
  {
    to: "/moments",
    tag: "Real bonds",
    title: "Anubandh Moments",
    body: "Real couples who found their bond through Anubandh.ai — no stock clichés.",
    icon: Heart,
    testid: "explore-moments",
  },
  {
    to: "/subscription",
    tag: "Plans",
    title: "Simple pricing",
    body: "Free to start. Priced for the decision, not the search. Cancel anytime.",
    icon: Star,
    testid: "explore-pricing",
  },
];

const FADE_UP = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" } };

export default function Landing() {
  usePageMeta(
    "India's Marriage Intelligence Platform",
    "India's first AI-powered Marriage Decision Intelligence. Explainable compatibility, Kundali × AI Fusion, and family-shareable insights — for the highest-stakes decision of your life."
  );
  const stats = useMemo(() => STATS, []);
  const [testimonials, setTestimonials] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/testimonials");
        setTestimonials(data.testimonials || []);
      } catch {
        // Fallback to hardcoded testimonials rendered below.
      }
    })();
  }, []);

  const fallbackT = [
    { couple: "Rohit & Priyanka", city: "Bengaluru → Pune", quote: "We matched in 9 days. The AI report showed us where we'd struggle before we ever argued. Our families read it together — the conversation changed everything.", rating: 5 },
    { couple: "Aditya & Meghana", city: "Hyderabad", quote: "I'd been on three matrimony sites for two years. Anubandh gave me one thoughtful match a week — and the fifth one is now my fiancée.", rating: 5 },
    { couple: "Vikram & Shreya", city: "London → Delhi", quote: "As an NRI, cultural fit mattered as much as personality. The Family Fit section of the report gave my parents peace they never had before.", rating: 5 },
  ];
  const T = testimonials.length ? testimonials : fallbackT;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="w-full text-center text-xs tracking-[0.2em] uppercase py-2 text-amber-100" style={{ background: "#1E1B4B" }}>
        <span className="opacity-70">आपका अनुबंध लिखा जा चुका है</span>
        <span className="mx-3 opacity-40">·</span>
        <span>Your bond is already written. We help you read it.</span>
      </div>
      <Nav variant="marketing" />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10" style={{
          background: "radial-gradient(1200px 600px at 78% 30%, rgba(217,119,6,0.18), transparent 60%), radial-gradient(900px 500px at 15% 70%, rgba(30,27,75,0.10), transparent 55%), #F9F8F6",
        }} />
        <StarField />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 lg:pt-24 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="lg:col-span-7 relative">
            <div className="devanagari text-amber-700 text-sm sm:text-base tracking-wide" data-testid="hero-shloka">विवाहः स्वर्गे निर्मीयते</div>
            <div className="overline mt-3" data-testid="hero-overline">Marriages are made in heaven</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-[4.25rem] tracking-tight leading-[1.02] text-[#1E1B4B] mt-6 font-medium">
              Some are written<br />
              in the <span className="italic text-amber-700">stars.</span><br />
              We help you <span className="relative">read them<span className="absolute -right-6 -top-4 text-amber-500"><Sparkles size={20} /></span></span>.
            </h1>
            <p className="mt-8 text-lg text-stone-600 max-w-xl leading-relaxed">
              Anubandh.ai is India&apos;s Marriage Intelligence Platform — reading psychology, values,
              Vedic Kundali, and family alignment to reveal the match already meant for you. No
              endless browsing. Just clarity.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/signup" data-testid="hero-cta-begin" className="btn-pill text-white px-7 py-4 text-base glow-gold" style={{ background: "#1E1B4B" }}>
                Begin Your Intelligence Assessment <ChevronRight className="inline ml-1" size={18} />
              </Link>
              <Link to="/how-it-works" data-testid="hero-cta-how" className="btn-pill text-[#1E1B4B] border border-stone-300 px-7 py-4 text-base hover:bg-white">
                How it works
              </Link>
            </div>
            <div className="mt-14 flex items-center flex-wrap gap-x-8 gap-y-3 text-sm text-stone-500">
              <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-amber-700" /> KYC-verified profiles</div>
              <div className="flex items-center gap-2"><BrainCircuit size={16} className="text-amber-700" /> Explainable AI</div>
              <div className="flex items-center gap-2"><Moon size={16} className="text-amber-700" /> Kundali × AI Fusion</div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.2 }} className="lg:col-span-5 relative">
            <VideoHero />
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}
              className="absolute -bottom-8 -left-6 sm:-left-10 w-[86%] bg-white rounded-2xl p-5 border border-stone-200 shadow-[0_20px_50px_-20px_rgba(30,27,75,0.35)]">
              <div className="overline mb-1">Fusion Snapshot</div>
              <div className="flex items-baseline gap-3">
                <span className="font-serif-display text-4xl text-[#1E1B4B]">87%</span>
                <span className="text-sm text-stone-500">Kundali × AI</span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                {[["Vedic", 78], ["Values", 92], ["Family", 84]].map(([k, v]) => (
                  <div key={k} className="border border-stone-200 rounded-lg p-2">
                    <div className="text-stone-500">{k}</div>
                    <div className="text-[#1E1B4B] font-medium">{v}%</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>

        <div className="border-y border-stone-200 bg-white/70 backdrop-blur">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 grid grid-cols-2 lg:grid-cols-5 gap-6">
            {stats.map((s) => (
              <div key={s.l}>
                <div className="font-serif-display text-2xl lg:text-3xl text-[#1E1B4B]">{s.n}</div>
                <div className="text-xs uppercase tracking-widest text-stone-500 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUR PILLARS — compact */}
      <section className="py-20 relative overflow-hidden" style={{ background: "#12103A" }}>
        <StarField />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
          <div className="max-w-2xl">
            <div className="overline" style={{ color: "#FCD34D" }}>Four pillars</div>
            <h2 className="font-serif-display text-3xl lg:text-5xl text-white mt-3 leading-tight">
              Sacred belief. Sound science. <span className="italic text-amber-300">One quiet method.</span>
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="rounded-2xl p-6 border border-white/10 bg-white/[0.03] backdrop-blur"
              >
                <p.icon className="text-amber-300" size={22} />
                <h3 className="font-serif-display text-xl text-white mt-4">{p.title}</h3>
                <p className="text-sm text-stone-400 mt-2 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SAMPLE REPORT PREVIEW — aspirational, portfolio-report feel */}
      <section className="py-24 relative overflow-hidden bg-[#F9F8F6]">
        <div className="absolute inset-0 -z-10" style={{
          background: "radial-gradient(900px 500px at 50% 0%, rgba(217,119,6,0.10), transparent 60%), #F9F8F6",
        }} />
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="overline">A glimpse inside</div>
            <h2 className="font-serif-display text-3xl lg:text-5xl text-[#1E1B4B] mt-3 leading-tight">
              Your Marriage Intelligence Report<br />
              <span className="italic text-amber-700">— sample preview.</span>
            </h2>
            <p className="mt-5 text-stone-600 leading-relaxed">
              Rigorous, private, and written for the biggest decision of your life. Complete your assessment to unlock your own.
            </p>
          </div>

          <div className="mt-14 relative">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="relative rounded-[2rem] border border-stone-200 bg-white shadow-[0_40px_100px_-40px_rgba(30,27,75,0.35)] overflow-hidden"
              data-testid="sample-report-card"
            >
              {/* Report header */}
              <div className="border-b border-stone-200 px-8 lg:px-12 py-6 flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-stone-500">Anubandh · AI</div>
                  <div className="font-serif-display text-xl text-[#1E1B4B] mt-1">Marriage Intelligence Report</div>
                </div>
                <div className="flex items-center gap-3 text-xs text-stone-500">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Verified sample
                  </span>
                  <span className="hidden sm:inline">Report #A-0714</span>
                </div>
              </div>

              {/* Visible section — score + two categories + insight */}
              <div className="px-8 lg:px-12 py-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                {/* Overall score */}
                <div className="lg:col-span-4">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-stone-500">Overall Compatibility</div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="font-serif-display text-7xl lg:text-8xl text-[#1E1B4B] leading-none" data-testid="sample-score">87<span className="text-amber-700">%</span></span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700">
                    <TrendingUp size={14} />
                    <span>Exceptionally strong alignment</span>
                  </div>
                  <div className="mt-6 h-1.5 w-full rounded-full bg-stone-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: "87%", background: "linear-gradient(90deg, #1E1B4B, #D97706)" }} />
                  </div>
                  <div className="mt-2 text-xs text-stone-500">Weighted 60/40 · Lifestyle & Values / Professional</div>
                </div>

                {/* Two categories */}
                <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-stone-200 p-5 bg-gradient-to-br from-amber-50 to-white">
                    <div className="flex items-center gap-2 text-amber-800">
                      <Heart size={16} />
                      <div className="text-xs uppercase tracking-widest">Lifestyle &amp; Values</div>
                    </div>
                    <div className="mt-3 flex items-baseline justify-between">
                      <span className="font-serif-display text-4xl text-[#1E1B4B]">92%</span>
                      <span className="text-xs text-stone-500">weight 60%</span>
                    </div>
                    <div className="mt-3 h-1 w-full rounded-full bg-white overflow-hidden">
                      <div className="h-full rounded-full bg-amber-600" style={{ width: "92%" }} />
                    </div>
                    <div className="mt-4 text-xs text-stone-600 leading-relaxed">
                      Family orientation, spiritual outlook, daily rhythms and long-term life design.
                    </div>
                  </div>

                  <div className="rounded-2xl border border-stone-200 p-5 bg-gradient-to-br from-indigo-50 to-white">
                    <div className="flex items-center gap-2 text-[#1E1B4B]">
                      <Briefcase size={16} />
                      <div className="text-xs uppercase tracking-widest">Professional Compatibility</div>
                    </div>
                    <div className="mt-3 flex items-baseline justify-between">
                      <span className="font-serif-display text-4xl text-[#1E1B4B]">81%</span>
                      <span className="text-xs text-stone-500">weight 40%</span>
                    </div>
                    <div className="mt-3 h-1 w-full rounded-full bg-white overflow-hidden">
                      <div className="h-full rounded-full bg-[#1E1B4B]" style={{ width: "81%" }} />
                    </div>
                    <div className="mt-4 text-xs text-stone-600 leading-relaxed">
                      Ambitions, work rhythm, financial values and educational background alignment.
                    </div>
                  </div>
                </div>
              </div>

              {/* Insight line */}
              <div className="px-8 lg:px-12 pb-8">
                <div className="rounded-xl border-l-4 border-amber-500 bg-amber-50/60 pl-5 pr-6 py-4">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-amber-800">Key Insight</div>
                  <p className="mt-1.5 font-serif-display text-lg lg:text-xl text-[#1E1B4B] leading-snug" data-testid="sample-insight">
                    &ldquo;Your profile shows high alignment in long-term goals and family values.&rdquo;
                  </p>
                </div>
              </div>

              {/* Locked section — blurred */}
              <div className="relative border-t border-stone-200">
                <div
                  className="px-8 lg:px-12 py-10 space-y-5 select-none pointer-events-none"
                  aria-hidden
                  style={{ filter: "blur(7px)", WebkitFilter: "blur(7px)" }}
                >
                  <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-stone-500">Dimension breakdown</div>
                    <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
                      {["Personality", "Emotional Intelligence", "Communication", "Family Values", "Financial", "Life Priorities", "Traditions", "Personal Growth"].map((k) => (
                        <div key={k} className="rounded-lg border border-stone-200 p-3">
                          <div className="text-xs text-stone-500">{k}</div>
                          <div className="text-[#1E1B4B] font-medium mt-1">— %</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2 pt-4">
                    <div className="h-3 rounded bg-stone-200 w-11/12" />
                    <div className="h-3 rounded bg-stone-200 w-10/12" />
                    <div className="h-3 rounded bg-stone-200 w-9/12" />
                    <div className="h-3 rounded bg-stone-200 w-11/12" />
                    <div className="h-3 rounded bg-stone-200 w-8/12" />
                  </div>
                  <div className="pt-6">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-stone-500">Kundali × AI Fusion</div>
                    <div className="mt-3 grid grid-cols-3 gap-3">
                      {[1, 2, 3].map((k) => (
                        <div key={k} className="h-24 rounded-xl bg-stone-100 border border-stone-200" />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Overlay: lock + CTA */}
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 px-6 text-center bg-gradient-to-b from-white/50 via-white/85 to-white">
                  <div className="w-14 h-14 rounded-full grid place-items-center shadow-lg" style={{ background: "#1E1B4B" }}>
                    <Lock className="text-amber-400" size={22} />
                  </div>
                  <div className="max-w-md">
                    <div className="font-serif-display text-2xl lg:text-3xl text-[#1E1B4B]">Your full report awaits.</div>
                    <p className="mt-2 text-sm text-stone-600 leading-relaxed">
                      Eight dimensions, Kundali × AI fusion, family-fit narrative, and a private conversation guide — written just for you.
                    </p>
                  </div>
                  <Link
                    to="/signup"
                    data-testid="sample-unlock-cta"
                    className="btn-pill text-white px-8 py-4 text-base glow-gold inline-flex items-center gap-2"
                    style={{ background: "#1E1B4B" }}
                  >
                    <Lock size={16} /> Unlock Your Full Report
                    <ChevronRight size={18} />
                  </Link>
                  <div className="text-xs text-stone-500">Free assessment · No credit card · Private &amp; encrypted</div>
                </div>
              </div>
            </motion.div>

            {/* Trust row below */}
            <div className="mt-8 flex items-center justify-center flex-wrap gap-x-8 gap-y-3 text-xs text-stone-500">
              <div className="flex items-center gap-2"><ShieldCheck size={14} className="text-amber-700" /> Reports never shared without your consent</div>
              <div className="flex items-center gap-2"><BrainCircuit size={14} className="text-amber-700" /> Reviewed by relationship scientists</div>
              <div className="flex items-center gap-2"><Moon size={14} className="text-amber-700" /> Vedic Kundali cross-checked</div>
            </div>
          </div>
        </div>
      </section>

      {/* EXPLORE — links to dedicated pages */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline">Explore Anubandh.ai</div>
            <h2 className="font-serif-display text-3xl lg:text-5xl text-[#1E1B4B] mt-3 leading-tight">
              Six doorways.<br /><span className="italic text-amber-700">One clear method.</span>
            </h2>
          </div>
          <p className="text-sm text-stone-500 max-w-sm">
            Each page is a slow, honest walk through one part of the Anubandh method. Read what
            resonates. Skip what doesn&apos;t.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {EXPLORE.map((e, i) => (
            <motion.div
              key={e.to}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
            >
              <Link
                to={e.to}
                data-testid={e.testid}
                className="group block rounded-2xl border border-stone-200 bg-white p-7 hover:border-amber-300 hover:shadow-[0_20px_50px_-25px_rgba(30,27,75,0.25)] transition-all"
              >
                <div className="flex items-start justify-between">
                  <e.icon className="text-amber-700" size={22} />
                  <ArrowRight size={16} className="text-stone-300 group-hover:text-amber-700 group-hover:translate-x-1 transition-all" />
                </div>
                <div className="overline mt-5">{e.tag}</div>
                <h3 className="font-serif-display text-2xl text-[#1E1B4B] mt-2">{e.title}</h3>
                <p className="text-sm text-stone-600 mt-3 leading-relaxed">{e.body}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="overline">Real bonds · Real reports</div>
            <h2 className="font-serif-display text-3xl lg:text-5xl text-[#1E1B4B] mt-3 leading-tight">
              The stars aligned.<br /><span className="italic text-amber-700">The numbers agreed.</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {T.slice(0, 3).map((t, i) => (
              <motion.div
                key={t.couple || t.names || i}
                {...FADE_UP}
                transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-stone-200 bg-[#F9F8F6] p-6"
              >
                <Quote className="text-amber-600" size={18} />
                <p className="mt-3 text-stone-700 leading-relaxed text-[15px]">&ldquo;{t.quote || t.text}&rdquo;</p>
                <div className="mt-5 pt-5 border-t border-stone-200 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-[#1E1B4B] text-sm">{t.couple || t.names}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{t.city}</div>
                  </div>
                  <div className="flex text-amber-500">
                    {Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={`rating-star-${k}`} size={12} fill="currentColor" />)}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/moments" data-testid="testimonials-see-moments" className="inline-flex items-center gap-2 text-amber-700 underline-grow">
              See real Anubandh moments <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 relative overflow-hidden" style={{ background: "#12103A" }}>
        <StarField />
        <div className="max-w-4xl mx-auto px-6 text-center relative text-white">
          <div className="devanagari text-amber-300">जो लिखा है, वो पढ़ लीजिए</div>
          <h2 className="font-serif-display text-3xl lg:text-5xl mt-4 leading-tight">
            Your bond is already written.<br /><span className="italic text-amber-300">Come, let&apos;s read it together.</span>
          </h2>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/signup" data-testid="final-cta-begin" className="btn-pill bg-amber-500 text-[#12103A] font-medium px-8 py-4 text-base inline-flex items-center gap-2">
              Begin Your Intelligence Assessment <ChevronRight size={18} />
            </Link>
            <Link to="/subscription" data-testid="final-cta-plans" className="btn-pill border border-white/25 text-white px-8 py-4 text-base hover:bg-white/10">
              See plans
            </Link>
          </div>
          <p className="mt-6 text-stone-400 text-sm">Free to start · No credit card required · ID verification in 3 minutes</p>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
